import apiService from './api';
import type {
  Project,
  ProjectCreateData,
  Sample,
  TestResult,
  ProjectStatistics,
  LockStatus,
} from '../types/api';

export const projectService = {
  /**
   * 获取项目列表
   */
  async getProjects(): Promise<Project[]> {
    const response = await apiService.get<any>('/projects/');
    // 处理Django REST Framework的分页响应
    if (response && typeof response === 'object' && 'results' in response) {
      return response.results || [];
    }
    // 如果响应直接是数组，直接返回
    if (Array.isArray(response)) {
      return response;
    }
    // 其他情况返回空数组
    return [];
  },

  /**
   * 获取单个项目
   */
  async getProject(id: string): Promise<Project> {
    return await apiService.get<Project>(`/projects/${id}/`);
  },

  /**
   * 创建项目
   */
  async createProject(data: ProjectCreateData): Promise<Project> {
    const response = await apiService.post<{ message: string; project: Project }>('/projects/', data);
    return response.project;
  },

  /**
   * 更新项目
   */
  async updateProject(id: string, data: Partial<ProjectCreateData>): Promise<Project> {
    const response = await apiService.put<{ message: string; project: Project }>(`/projects/${id}/`, data);
    return response.project;
  },

  /**
   * 删除项目
   */
  async deleteProject(id: string): Promise<void> {
    await apiService.delete(`/projects/${id}/`);
  },

  /**
   * 获取项目的样本数据
   */
  async getSamples(projectId: string): Promise<Sample[]> {
    const response = await apiService.get<any>(`/projects/${projectId}/samples/`);
    // 处理Django REST Framework的分页响应
    if (response && typeof response === 'object' && 'results' in response) {
      return response.results || [];
    }
    // 如果响应直接是数组，直接返回
    if (Array.isArray(response)) {
      return response;
    }
    // 其他情况返回空数组
    return [];
  },

  /**
   * 上传样本图片
   */
  async uploadSample(projectId: string, data: {
    object_name: string;
    image: File;
  }): Promise<Sample> {
    const formData = new FormData();
    formData.append('object_name', data.object_name);
    formData.append('image_file', data.image);

    const response = await apiService.post<{ message: string; sample: Sample }>(
      `/projects/${projectId}/samples/`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.sample;
  },

  /**
   * 删除样本
   */
  async deleteSample(projectId: string, sampleId: number): Promise<void> {
    return await apiService.delete<void>(`/projects/${projectId}/samples/${sampleId}/delete/`);
  },

  /**
   * 训练模型
   */
  async trainModel(projectId: string): Promise<{
    status: string;
    accuracy: number;
    message: string;
  }> {
    return await apiService.post(`/projects/${projectId}/train/`, {});
  },

  /**
   * 测试模型
   */
  async testModel(projectId: string, data: {
    image: File;
  }): Promise<TestResult> {
    const formData = new FormData();
    formData.append('project_id', projectId);
    formData.append('image', data.image);

    return await apiService.post<TestResult>(
      '/projects/test/',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
  },

  /**
   * 获取测试结果
   */
  async getTestResults(projectId: string): Promise<TestResult[]> {
    const response = await apiService.get<any>(`/projects/${projectId}/test-results/`);
    // 处理Django REST Framework的分页响应
    if (response && typeof response === 'object' && 'results' in response) {
      return response.results || [];
    }
    // 如果响应直接是数组，直接返回
    if (Array.isArray(response)) {
      return response;
    }
    // 其他情况返回空数组
    return [];
  },

  /**
   * 获取项目统计
   */
  async getStatistics(projectId: string): Promise<ProjectStatistics> {
    return await apiService.get<ProjectStatistics>(`/projects/${projectId}/statistics/`);
  },

  // ── 项目互斥锁 ──

  /**
   * 获取项目锁
   */
  async acquireLock(projectId: string): Promise<LockStatus> {
    return await apiService.post<LockStatus>(`/projects/${projectId}/lock/acquire/`, {});
  },

  /**
   * 释放项目锁
   */
  async releaseLock(projectId: string): Promise<LockStatus> {
    return await apiService.post<LockStatus>(`/projects/${projectId}/lock/release/`, {});
  },

  /**
   * 锁心跳
   */
  async heartbeatLock(projectId: string): Promise<LockStatus> {
    return await apiService.post<LockStatus>(`/projects/${projectId}/lock/heartbeat/`, {});
  },
};